import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { 
  Calculator, Search, TrendingUp, TrendingDown, 
  ChefHat, Flame, Package, Scale, Info, X, Plus,
  AlertTriangle, CheckCircle, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

// Helper per ottenere headers con tenant
const getAuthHeaders = () => {
  const token = localStorage.getItem('token') || localStorage.getItem('tenant_token');
  const headers = { 'Authorization': `Bearer ${token}` };
  
  let tenantDb = localStorage.getItem('tenant_db');
  if (!tenantDb) {
    try {
      const tenantList = JSON.parse(localStorage.getItem('tenant_list') || '[]');
      if (tenantList.length > 0 && tenantList[0].database_name) {
        tenantDb = tenantList[0].database_name;
      }
    } catch (e) {}
  }
  if (!tenantDb) {
    try {
      const tenantUser = JSON.parse(localStorage.getItem('tenant_user') || '{}');
      if (tenantUser.tenants && tenantUser.tenants[0]) {
        tenantDb = tenantUser.tenants[0].database_name;
      }
    } catch (e) {}
  }
  
  if (tenantDb) {
    headers['X-Tenant-DB'] = tenantDb.replace(/^tenant_/, '');
  }
  return headers;
};

// Tipi di vendita con spiegazione
const SALE_TYPES = [
  { value: 'PESO_CRUDO', label: 'Peso Crudo', desc: 'Il cliente paga il peso prima della cottura' },
  { value: 'PESO_COTTO', label: 'Peso Cotto', desc: 'Il cliente paga il peso dopo la cottura' },
  { value: 'PORZIONE', label: 'Porzione', desc: 'Vendita a porzione fissa' },
  { value: 'PEZZO', label: 'Pezzo', desc: 'Vendita al pezzo (es. 1 orata intera)' }
];

// Metodi di cottura comuni
const COMMON_COOKING_METHODS = [
  'crudo', 'alla_griglia', 'in_padella', 'al_forno', 'bollito', 'fritto',
  'arrosto', 'brasato', 'al_vapore', 'sottovuoto', 'stufato'
];

const FoodCostCalculator = () => {
  // Stato per la ricerca prodotti
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  
  // Stato per il calcolo
  const [calcInput, setCalcInput] = useState({
    ingredient_name: '',
    purchase_price_per_kg: '',
    quantity_grams: '',
    cooking_method: '',
    sale_type: '',
    formato: '',
    stato: ''
  });
  const [calcResult, setCalcResult] = useState(null);
  const [calculating, setCalculating] = useState(false);
  
  // Statistiche database
  const [dbStats, setDbStats] = useState(null);

  // Carica statistiche al mount
  useEffect(() => {
    fetchDatabaseStats();
  }, []);

  const fetchDatabaseStats = async () => {
    try {
      const res = await axios.get(`${API}/gestione/ricette/food-cost/database-stats`, {
        headers: getAuthHeaders()
      });
      setDbStats(res.data.stats);
    } catch (err) {
      console.error('Errore caricamento stats:', err);
    }
  };

  // Ricerca prodotti con debounce
  const searchProducts = useCallback(async (term) => {
    if (!term || term.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setSearching(true);
    try {
      const res = await axios.get(`${API}/gestione/ricette/food-cost/search`, {
        params: { q: term, limit: 10 },
        headers: getAuthHeaders()
      });
      setSearchResults(res.data.results || []);
    } catch (err) {
      console.error('Errore ricerca:', err);
    } finally {
      setSearching(false);
    }
  }, []);

  // Debounce della ricerca
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm) searchProducts(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, searchProducts]);

  // Seleziona prodotto dalla ricerca
  const selectProduct = (product) => {
    setSelectedProduct(product);
    setCalcInput(prev => ({
      ...prev,
      ingredient_name: product.prodotto,
      formato: product.formato,
      stato: product.stato,
      sale_type: product.tipo_vendita_default || ''
    }));
    setSearchTerm('');
    setSearchResults([]);
  };

  // Calcola food cost
  const calculateFoodCost = async () => {
    if (!calcInput.ingredient_name || !calcInput.purchase_price_per_kg || !calcInput.quantity_grams) {
      toast.error('Compila nome, prezzo e quantità');
      return;
    }

    setCalculating(true);
    try {
      const res = await axios.post(`${API}/gestione/ricette/food-cost/calculate`, {
        ingredient_name: calcInput.ingredient_name,
        purchase_price_per_kg: parseFloat(calcInput.purchase_price_per_kg),
        quantity_grams: parseFloat(calcInput.quantity_grams),
        cooking_method: calcInput.cooking_method || null,
        sale_type: calcInput.sale_type || null,
        formato: calcInput.formato || null,
        stato: calcInput.stato || null
      }, {
        headers: getAuthHeaders()
      });
      
      setCalcResult(res.data.calculation);
      toast.success('Food cost calcolato!');
    } catch (err) {
      toast.error('Errore nel calcolo');
      console.error(err);
    } finally {
      setCalculating(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setSelectedProduct(null);
    setCalcInput({
      ingredient_name: '',
      purchase_price_per_kg: '',
      quantity_grams: '',
      cooking_method: '',
      sale_type: '',
      formato: '',
      stato: ''
    });
    setCalcResult(null);
  };

  return (
    <div className="space-y-6">
      {/* Header con statistiche */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Calculator className="w-6 h-6 text-emerald-400" />
            Calcolo Food Cost Reale
          </h2>
          <p className="text-zinc-400 text-sm mt-1">
            Considera resa mondatura e cottura per un costo preciso
          </p>
        </div>
        
        {dbStats && (
          <div className="flex items-center gap-2 px-3 py-2 bg-zinc-800/50 rounded-lg border border-zinc-700/50">
            <Package className="w-4 h-4 text-emerald-400" />
            <span className="text-sm text-zinc-300">
              <strong>{dbStats.total_products}</strong> prodotti in database
            </span>
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* FORM CALCOLO */}
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <ChefHat className="w-5 h-5 text-amber-400" />
              Calcola Food Cost
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Ricerca ingrediente */}
            <div className="relative">
              <label className="text-sm text-zinc-400 mb-1 block">Ingrediente</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  placeholder="Cerca ingrediente..."
                  value={selectedProduct ? selectedProduct.prodotto : searchTerm}
                  onChange={(e) => {
                    if (selectedProduct) {
                      setSelectedProduct(null);
                      setCalcInput(prev => ({ ...prev, ingredient_name: '', formato: '', stato: '' }));
                    }
                    setSearchTerm(e.target.value);
                  }}
                  className="pl-10 bg-zinc-800/50 border-zinc-700 text-white"
                />
                {selectedProduct && (
                  <button
                    onClick={() => {
                      setSelectedProduct(null);
                      setSearchTerm('');
                      setCalcInput(prev => ({ ...prev, ingredient_name: '', formato: '', stato: '' }));
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-zinc-700 rounded"
                  >
                    <X className="w-4 h-4 text-zinc-400" />
                  </button>
                )}
              </div>
              
              {/* Dropdown risultati ricerca */}
              {searchResults.length > 0 && !selectedProduct && (
                <div className="absolute z-50 w-full mt-1 bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl max-h-60 overflow-auto">
                  {searchResults.map((product, idx) => (
                    <button
                      key={idx}
                      onClick={() => selectProduct(product)}
                      className="w-full px-4 py-2 text-left hover:bg-zinc-700/50 flex justify-between items-center border-b border-zinc-700/50 last:border-0"
                    >
                      <div>
                        <span className="text-white font-medium">{product.prodotto}</span>
                        <span className="text-xs text-zinc-500 ml-2">
                          {product.formato} • {product.stato}
                        </span>
                      </div>
                      <span className="text-xs text-emerald-400">
                        Resa: {Math.round(product.resa_mondatura * 100)}%
                      </span>
                    </button>
                  ))}
                </div>
              )}
              
              {searching && (
                <div className="absolute right-10 top-1/2 -translate-y-1/2">
                  <div className="animate-spin w-4 h-4 border-2 border-emerald-400 border-t-transparent rounded-full" />
                </div>
              )}
            </div>

            {/* Se non trovato, inserimento manuale */}
            {!selectedProduct && searchTerm.length >= 2 && searchResults.length === 0 && !searching && (
              <div className="flex items-center gap-2 p-2 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" />
                <span className="text-xs text-amber-300">
                  Prodotto non in database. Inserisci manualmente i dati.
                </span>
              </div>
            )}

            {/* Prodotto selezionato info */}
            {selectedProduct && (
              <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                <div className="flex items-center gap-2 text-emerald-400 mb-2">
                  <CheckCircle className="w-4 h-4" />
                  <span className="text-sm font-medium">Prodotto trovato nel database</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="text-zinc-400">
                    Categoria: <span className="text-white">{selectedProduct.categoria}</span>
                  </div>
                  <div className="text-zinc-400">
                    Formato: <span className="text-white">{selectedProduct.formato}</span>
                  </div>
                  <div className="text-zinc-400">
                    Resa Mondatura: <span className="text-emerald-400 font-medium">{Math.round(selectedProduct.resa_mondatura * 100)}%</span>
                  </div>
                  <div className="text-zinc-400">
                    Vendita default: <span className="text-white">{selectedProduct.tipo_vendita_default}</span>
                  </div>
                </div>
                {selectedProduct.note && (
                  <p className="text-xs text-zinc-500 mt-2 italic">{selectedProduct.note}</p>
                )}
              </div>
            )}

            {/* Prezzo e quantità */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Prezzo (€/kg)</label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="20.00"
                  value={calcInput.purchase_price_per_kg}
                  onChange={(e) => setCalcInput(prev => ({ ...prev, purchase_price_per_kg: e.target.value }))}
                  className="bg-zinc-800/50 border-zinc-700 text-white"
                />
              </div>
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Quantità (g)</label>
                <Input
                  type="number"
                  placeholder="300"
                  value={calcInput.quantity_grams}
                  onChange={(e) => setCalcInput(prev => ({ ...prev, quantity_grams: e.target.value }))}
                  className="bg-zinc-800/50 border-zinc-700 text-white"
                />
              </div>
            </div>

            {/* Metodo cottura */}
            <div>
              <label className="text-sm text-zinc-400 mb-1 flex items-center gap-1">
                <Flame className="w-3 h-3" />
                Metodo Cottura
              </label>
              <select
                value={calcInput.cooking_method}
                onChange={(e) => setCalcInput(prev => ({ ...prev, cooking_method: e.target.value }))}
                className="w-full px-3 py-2 bg-zinc-800/50 border border-zinc-700 rounded-md text-white text-sm"
              >
                <option value="">-- Seleziona (opzionale) --</option>
                {selectedProduct?.resa_cottura ? (
                  Object.keys(selectedProduct.resa_cottura).map(method => (
                    <option key={method} value={method}>
                      {method.replace(/_/g, ' ')} ({Math.round(selectedProduct.resa_cottura[method] * 100)}%)
                    </option>
                  ))
                ) : (
                  COMMON_COOKING_METHODS.map(method => (
                    <option key={method} value={method}>{method.replace(/_/g, ' ')}</option>
                  ))
                )}
              </select>
            </div>

            {/* Tipo vendita */}
            <div>
              <label className="text-sm text-zinc-400 mb-1 flex items-center gap-1">
                <Scale className="w-3 h-3" />
                Tipo Vendita
              </label>
              <select
                value={calcInput.sale_type}
                onChange={(e) => setCalcInput(prev => ({ ...prev, sale_type: e.target.value }))}
                className="w-full px-3 py-2 bg-zinc-800/50 border border-zinc-700 rounded-md text-white text-sm"
              >
                <option value="">-- Default del prodotto --</option>
                {SALE_TYPES.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
              {calcInput.sale_type && (
                <p className="text-xs text-zinc-500 mt-1">
                  {SALE_TYPES.find(t => t.value === calcInput.sale_type)?.desc}
                </p>
              )}
            </div>

            {/* Bottoni */}
            <div className="flex gap-2 pt-2">
              <Button
                onClick={calculateFoodCost}
                disabled={calculating || !calcInput.ingredient_name || !calcInput.purchase_price_per_kg || !calcInput.quantity_grams}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {calculating ? (
                  <span className="flex items-center gap-2">
                    <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                    Calcolo...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Calculator className="w-4 h-4" />
                    Calcola
                  </span>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={resetForm}
                className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              >
                Reset
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* RISULTATO CALCOLO */}
        <Card className={`border transition-all duration-300 ${
          calcResult 
            ? 'bg-gradient-to-br from-zinc-900 to-emerald-900/20 border-emerald-700/50' 
            : 'bg-zinc-900/50 border-zinc-800'
        }`}>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-emerald-400" />
              Risultato Food Cost
            </CardTitle>
          </CardHeader>
          <CardContent>
            {calcResult ? (
              <div className="space-y-4">
                {/* Header risultato */}
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-bold text-white">{calcResult.ingredient}</h3>
                    {calcResult.found_in_db && (
                      <p className="text-xs text-zinc-400">
                        {calcResult.db_category} • {calcResult.db_subcategory} • {calcResult.db_format}
                      </p>
                    )}
                  </div>
                  {calcResult.found_in_db ? (
                    <span className="px-2 py-1 text-xs bg-emerald-500/20 text-emerald-400 rounded-full">
                      ✓ In database
                    </span>
                  ) : (
                    <span className="px-2 py-1 text-xs bg-amber-500/20 text-amber-400 rounded-full">
                      Valori default
                    </span>
                  )}
                </div>

                {/* Rese */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-zinc-800/50 rounded-lg text-center">
                    <p className="text-xs text-zinc-400 mb-1">Resa Mondatura</p>
                    <p className="text-2xl font-bold text-amber-400">{calcResult.yield_mondatura_pct}</p>
                    <p className="text-xs text-zinc-500">scarto preparazione</p>
                  </div>
                  <div className="p-3 bg-zinc-800/50 rounded-lg text-center">
                    <p className="text-xs text-zinc-400 mb-1">Resa Cottura</p>
                    <p className="text-2xl font-bold text-orange-400">{calcResult.yield_cottura_pct}</p>
                    <p className="text-xs text-zinc-500">
                      {calcResult.cooking_method_used ? calcResult.cooking_method_used.replace(/_/g, ' ') : 'media'}
                    </p>
                  </div>
                </div>

                {/* Confronto prezzi */}
                <div className="p-4 bg-zinc-800/30 rounded-lg space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-zinc-400">Prezzo acquisto</span>
                    <span className="text-white font-medium">{calcResult.purchase_price_kg}€/kg</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-zinc-400 flex items-center gap-1">
                      <TrendingUp className="w-4 h-4 text-red-400" />
                      Prezzo REALE
                    </span>
                    <span className="text-xl font-bold text-red-400">{calcResult.real_price_kg}€/kg</span>
                  </div>
                  <hr className="border-zinc-700" />
                  <div className="flex justify-between items-center">
                    <span className="text-zinc-400">Quantità usata</span>
                    <span className="text-white">{calcResult.quantity_grams}g</span>
                  </div>
                  <div className="flex justify-between items-center text-lg">
                    <span className="text-emerald-400 font-medium">Costo ingrediente</span>
                    <span className="text-2xl font-bold text-emerald-400">{calcResult.cost_for_quantity}€</span>
                  </div>
                </div>

                {/* Info aggiuntive */}
                <div className="flex items-start gap-2 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                  <Info className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                  <div className="text-xs text-blue-300">
                    <p className="font-medium mb-1">Tipo vendita: {calcResult.sale_type}</p>
                    {calcResult.note && <p className="text-blue-200/70">{calcResult.note}</p>}
                  </div>
                </div>
              </div>
            ) : (
              <div className="h-64 flex flex-col items-center justify-center text-zinc-500">
                <Calculator className="w-12 h-12 mb-3 opacity-30" />
                <p>Inserisci i dati e clicca "Calcola"</p>
                <p className="text-xs mt-1">per vedere il food cost reale</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Info box spiegazione */}
      <Card className="bg-zinc-900/30 border-zinc-800">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-zinc-400">
              <p className="font-medium text-zinc-300 mb-1">Come funziona il calcolo?</p>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li><strong className="text-amber-400">Resa mondatura:</strong> Quanto resta dopo pulizia (es. branzino intero → 55% utilizzabile)</li>
                <li><strong className="text-orange-400">Resa cottura:</strong> Quanto resta dopo cottura (es. alla griglia → 82% del peso)</li>
                <li><strong className="text-red-400">Prezzo reale:</strong> = Prezzo acquisto ÷ Resa mondatura ÷ Resa cottura</li>
                <li><strong className="text-emerald-400">Tipo vendita:</strong> Peso crudo (cliente paga prima cottura) vs Peso cotto (dopo cottura)</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FoodCostCalculator;
